/*  -----------------------------------------------------------------
 *  src/components/edit-jobs-container.jsx
 *  -----------------------------------------------------------------
 *  Container-Komponente für die „Job bearbeiten“-Seite (Admin-Frontend)
 *  – nutzt KEIN TypeScript
 *  – übernimmt Einheit + DetailText automatisch aus dem Tasks-Katalog
 *  ----------------------------------------------------------------- */

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import { AppContext } from '../AppContext';
import EditJobsView from './edit-jobs-view';

/* ------------------------------------------------------------ */
/* Hilfsfunktionen                                              */
/* ------------------------------------------------------------ */
const RADIO_OPTIONS = [
  { label: 'Der Verdacht auf Kampfmittel hat sich nicht bestätigt',      value: 'option1' },
  { label: 'Vorgefundene Kampfmittel wurden beräumt / übergeben',        value: 'option2' },
  { label: 'Kampfmittel wurden nicht / nicht vollständig beräumt weil:', value: 'option3' },
  { label: 'Der Verdacht auf Kampfmittel hat sich bestätigt',            value: 'option4' },
  { label: 'Sonstiges',                                                  value: 'option5' },
  { label: 'Ungeklärt',                                                  value: 'option6' }
];

/* Optionen, bei denen das Anmerkungen-Textfeld erscheint und gespeichert wird (analog Step 4) */
const NOTE_OPTIONS = ['option3', 'option5'];

const buildTodoList = (rawJob) =>
  Object.entries(rawJob)
    .filter(([k]) => k.startsWith('TODO'))
    .map(([k, v]) => ({
      Position:      k.replace('TODO', '').padStart(3, '0'),
      Aufwand:       v.Aufwand      || '',
      Einheit:       v.Einheit      || '',
      Beschreibung:  v.Beschreibung || '',
      DetailText:    v.DetailText   || ''
    }));

/* ------------------------------------------------------------ */
const EditJobsContainer = () => {
  /* --- Kontext & Routing -------------------- */
  const { jobID } = useParams();
  const navigate  = useNavigate();
  const { showNotification, isGoogleMapsLoaded } = React.useContext(AppContext);

  /* --- Haupt-State -------------------------- */
  const formRef = React.useRef(null);
  const [loading, setLoading]               = React.useState(true);
  const [jobData, setJobData]               = React.useState(null);
  const [customerData, setCustomerData]     = React.useState(null);

  /* Einzel-States */
  const [contactPersons, setContactPersons] = React.useState([]);
  const [imagePreviews, setImagePreviews]   = React.useState([]);
  const [jobDocuments, setJobDocuments]     = React.useState([]);
  const [dataState, setDataState]           = React.useState([]);

  const [descriptionOptions, setDescriptionOptions] = React.useState([]);
  const [users, setUsers]                           = React.useState([]);

  const [selectedValue, setSelectedValue] = React.useState('');
  const [summaryText,  setSummaryText]    = React.useState('');

  /* UI-State */
  const [sort, setSort]                   = React.useState([{ field: 'Position', dir: 'asc' }]);
  const [selectedTab, setSelectedTab]     = React.useState(0);
  const [mapVisible, setMapVisible]       = React.useState(false);
  const [contactPersonsVisible, setContactPersonsVisible]           = React.useState(false);
  const [employeeAssignmentVisible, setEmployeeAssignmentVisible]   = React.useState(false);
  const [jobDocumentsVisible, setJobDocumentsVisible]               = React.useState(false);
  const [pdfLoading, setPdfLoading]       = React.useState(false);

  /* ------------------------------------------------------------ */
  /* Helper: Beschreibung → {Einheit, DetailText}                 */
  /* ------------------------------------------------------------ */
  const getTaskMeta = React.useCallback(
    (desc) => {
      const obj = descriptionOptions.find(o => o.Beschreibung === desc);
      return {
        Einheit:    obj ? obj.Einheit    || '' : '',
        DetailText: obj ? obj.DetailText || '' : ''
      };
    },
    [descriptionOptions]
  );

  /* ------------------------------------------------------------ */
  /* Initial-Load – Job, Customer, Tasks, Users                   */
  /* ------------------------------------------------------------ */
  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        /* ---------- Job ---------------- */
        const jobRes = await fetch(`${config.host}/jobs/${jobID}`);
        if (!jobRes.ok) throw new Error('Job nicht gefunden');
        const rawJob = await jobRes.json();

        /* ---------- Customer ----------- */
        const custRes = await fetch(`${config.host}/customers/${rawJob.CustomerID}`);
        const rawCust = custRes.ok ? await custRes.json() : null;

        /* ---------- Tasks-Katalog ------ */
        const tasksRes = await axios.get(`${config.host}/tasks`);
        const taskArr  = Array.isArray(tasksRes.data) ? tasksRes.data
                        : Object.values(tasksRes.data || {});
        setDescriptionOptions(
          taskArr.map(t => ({
            Beschreibung: typeof t === 'string' ? t : t.Beschreibung || '',
            Einheit:      typeof t === 'string' ? '' : t.Einheit      || '',
            DetailText:   typeof t === 'string' ? '' : t.DetailText   || ''
          }))
        );

        /* ---------- User-Liste --------- */
        const userRes  = await fetch(`${config.host}/users`);
        const rawUsers = await userRes.json();
        setUsers(rawUsers.map(u => ({
          value: u.BenutzerID,
          label: u.Benutzername,
          avatarThumbLink: u.AvatarThumbLink
        })));

        /* ---------- Einmalige States --- */
        setImagePreviews(rawJob.Media || []);
        setJobDocuments(Array.isArray(rawJob.JobDocuments) ? rawJob.JobDocuments : []);

        const selOpt = RADIO_OPTIONS.find(o => o.label === (rawJob.Summary || ''));
        setSelectedValue(selOpt?.value || '');
        setSummaryText(rawJob.SummaryNotes || '');

        setContactPersons(
          Array.isArray(rawJob.Ansprechpartner) && rawJob.Ansprechpartner.length
            ? rawJob.Ansprechpartner.map(p => ({
                name:    p.name    || (typeof p === 'string' ? p : ''),
                jobrole: p.jobrole || '',
                email:   p.email   || '',
                phone:   p.phone   || ''
              }))
            : [{ name:'', jobrole:'', email:'', phone:'' }]
        );

        setDataState(buildTodoList(rawJob));

        /* ---------- Job-Grunddaten ----- */
        const assignedEmployee =
          Array.isArray(rawJob.AssignedEmployee) ? rawJob.AssignedEmployee
          : rawJob.AssignedEmployee ? rawJob.AssignedEmployee.split(',').map(s => s.trim())
          : rawJob.employee ? [rawJob.employee]
          : [];

        const newFirma   = rawCust?.Firma || '';
        const newAP      = `${rawCust?.Titel || ''} ${rawCust?.Vorname || ''} ${rawCust?.Nachname || ''}`.trim();
        const newAdresse = `${rawCust?.Strasse || ''} ${rawCust?.Hausnummer || ''}, ${
          rawCust?.PLZ || ''
        } ${rawCust?.ORT || ''}`.trim();

        setJobData({
          auftragsnummer: rawJob.JobIDcustom || '',
          kurzbeschreibung: rawJob.Kurzbeschreibung || '',
          AusfuehrungszeitStart: rawJob.AusfuehrungszeitStart ? new Date(rawJob.AusfuehrungszeitStart) : null,
          AusfuehrungszeitEnde:  rawJob.AusfuehrungszeitEnde  ? new Date(rawJob.AusfuehrungszeitEnde)  : null,
          strasse:   rawJob.EinsatzStrasse   || '',
          hausnummer:rawJob.EinsatzHausnummer|| '',
          plz:       rawJob.EinsatzPLZ       || '',
          ort:       rawJob.EinsatzORT       || '',
          latitude:  rawJob.latitude         || '',
          longitude: rawJob.longitude        || '',
          AssignedEmployee: assignedEmployee,
          JobActive: rawJob.JobActive ?? true,
          Anmerkungen: rawJob.Anmerkungen || '',
          SummaryNotes: rawJob.SummaryNotes || '',
          Summary: rawJob.Summary || '',
          InternalNotes: rawJob.InternalNotes || '',
          firma: newFirma,
          ansprechpartner: newAP,
          adresse: newAdresse
        });

        setCustomerData(rawCust);
        setLoading(false);
      } catch (err) {
        console.error(err);
        showNotification('error', 'Fehler beim Laden der Daten.');
        navigate('/jobs');
      }
    };
    fetchInitialData();
  }, [jobID, navigate]);

  /* ------------------------------------------------------------ */
  /* Kundendaten ↔ jobData mergen                                 */
  /* ------------------------------------------------------------ */
  React.useEffect(() => {
    if (!customerData || !jobData) return;

    const newFirma          = customerData?.Firma || '';
    const newAnsprechpartner = `${customerData.Titel || ''} ${customerData.Vorname || ''} ${customerData.Nachname || ''}`.trim();
    const newAdresse        = `${customerData.Strasse || ''} ${customerData.Hausnummer || ''}, ${customerData.PLZ || ''} ${customerData.ORT || ''}`.trim();

    setJobData(prev => {
      if (
        prev.firma           === newFirma &&
        prev.ansprechpartner === newAnsprechpartner &&
        prev.adresse         === newAdresse
      ) return prev;
      return { ...prev, firma:newFirma, ansprechpartner:newAnsprechpartner, adresse:newAdresse };
    });
  }, [customerData]);

  /* ------------------------------------------------------------ */
  /* buildDiff – nur geänderte Felder                             */
  /* ------------------------------------------------------------ */
  const isDate = (v) => v instanceof Date && !isNaN(v);
  const isObj  = (v) => v && typeof v === 'object' && !Array.isArray(v) && !isDate(v);

  const buildDiff = (oldData = {}, newData = {}) => {
    const diff = {};

    Object.keys(newData).forEach((key) => {
      const oldVal = oldData[key];
      const newVal = newData[key];

      // Datum → ISO-String
      if (isDate(newVal)) {
        if (!isDate(oldVal) || newVal.getTime() !== oldVal.getTime()) {
          diff[key] = newVal.toISOString();
        }
        return;
      }

      // Flache Arrays vergleichen
      if (Array.isArray(newVal)) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          diff[key] = newVal;
        }
        return;
      }

      // Rekursiv in Objekten
      if (isObj(newVal)) {
        const nested = buildDiff(oldVal, newVal);
        if (Object.keys(nested).length) diff[key] = nested;
        return;
      }

      // Primitive Werte
      if ((newVal ?? '') !== (oldVal ?? '')) {
        diff[key] = newVal;
      }
    });

    return diff;
  };

  /* ------------------------------------------------------------ */
  /* Grid-Row-Aktionen (add / save / remove)                      */
  /* ------------------------------------------------------------ */
  const onRowAction = React.useCallback(async (options) => {
    let newState = [...dataState];

    /*  --- Helper: ergänzt Einheit & DetailText anhand Beschreibung --- */
    const completeItem = (item) => {
      const meta = getTaskMeta(item.Beschreibung);
      return {
        ...item,
        Einheit:    meta.Einheit || item.Einheit,
        DetailText: meta.DetailText
      };
    };

    const trans = (item, idx) => {
      const t = completeItem(item);
      return {
        POS:          (idx + 1).toString().padStart(3,'0'),
        Aufwand:      t.Aufwand,
        Einheit:      t.Einheit,
        Beschreibung: t.Beschreibung,
        DetailText:   t.DetailText
      };
    };

    switch (options.operation) {
      /* --------- remove --------- */
      case 'remove': {
        const delIdx = options.rowIndex;
        const delKey = `TODO${(delIdx + 1).toString().padStart(2,'0')}`;

        try {
          await axios.delete(`${config.host}/jobs/${encodeURIComponent(jobID)}/${delKey}`);
          showNotification('success', `${delKey} gelöscht.`);
        } catch (err) {
          console.error(err);
          showNotification('error', `${delKey} konnte nicht gelöscht werden.`);
          return;
        }

        /* Keys neu nummerieren */
        for (let i = delIdx + 1; i < newState.length; i++) {
          const newKey = `TODO${i.toString().padStart(2,'0')}`;
          await axios.put(`${config.host}/jobs/${encodeURIComponent(jobID)}`, {
            [newKey]: trans(newState[i], i - 1)
          });
        }
        const lastKey = `TODO${newState.length.toString().padStart(2,'0')}`;
        await axios.delete(`${config.host}/jobs/${encodeURIComponent(jobID)}/${lastKey}`);

        newState = newState
          .filter((_, idx) => idx !== delIdx)
          .map((it, idx) => ({ ...it, Position:(idx+1).toString().padStart(3,'0') }));
        setDataState(newState);
        break;
      }

      /* --------- save ---------- */
      case 'save': {
        const i = options.rowIndex;
        newState[i] = completeItem({ ...newState[i], Aufwand: options.dataItem.Aufwand });
        setDataState(newState);

        const key = `TODO${(i + 1).toString().padStart(2,'0')}`;
        await axios.put(`${config.host}/jobs/${encodeURIComponent(jobID)}`, { [key]: trans(newState[i], i) });
        showNotification('success', `${key} gespeichert.`);
        break;
      }

      /* --------- add ----------- */
      case 'add': {
        newState.push({
          Position: (newState.length + 1).toString().padStart(3,'0'),
          Aufwand: '',
          Einheit: '',
          Beschreibung: '',
          DetailText: ''
        });
        setDataState(newState);
        break;
      }

      default: return;
    }
  }, [dataState, jobID, showNotification, getTaskMeta]);

  /* ------------------------------------------------------------ */
  /* PDF-Download                                                 */
  /* ------------------------------------------------------------ */
  const downloadPdf = React.useCallback(async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(`${config.host}/download/pdf/${jobID}`, {
        responseType: 'blob'
      });
      const blob = new Blob(
        [res.data],
        { type: res.headers['content-type'] || 'application/pdf' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      /* Dateinamen aus Content-Disposition parsen (falls vorhanden) */
      const dispo = res.headers['content-disposition'];
      const match = dispo && dispo.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || 'download.pdf';

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error(err);
      showNotification('error', 'PDF Download fehlgeschlagen.');
    } finally {
      setPdfLoading(false);
    }
  }, [jobID, showNotification]);

  /* ------------------------------------------------------------ */
  /* Bild aus Fotodoku löschen                                    */
  /* ------------------------------------------------------------ */
  const removeImage = React.useCallback(async (index) => {
    const imageUrl = imagePreviews[index];
    try {
      await axios.delete(
        `${config.host}/delete-image/${encodeURIComponent(jobID)}`,
        {
          headers: { 'Content-Type': 'application/json' },
          data: { url: imageUrl }
        }
      );
      setImagePreviews(prev => prev.filter((_, i) => i !== index));
      showNotification('success', 'Bild gelöscht.');
    } catch (err) {
      console.error(err);
      showNotification('error', 'Fehler beim Löschen des Bildes.');
    }
  }, [imagePreviews, jobID, showNotification]);


  /* ------------------------------------------------------------ */
  /* Submit-Handler                                               */
  /* ------------------------------------------------------------ */
  /* ++++++++++++ NEU ab hier ++++++++++++ */
  const FIELD_MAP = {
    auftragsnummer:   'JobIDcustom',
    kurzbeschreibung: 'Kurzbeschreibung',
    strasse:          'EinsatzStrasse',
    hausnummer:       'EinsatzHausnummer',
    plz:              'EinsatzPLZ',
    ort:              'EinsatzORT',
    latitude:         'latitude',
    longitude:        'longitude',
    InternalNotes:    'InternalNotes'          // interne Notizen
  };

  const renameKeys = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [FIELD_MAP[k] || k, v])
    );
  /* ++++++++++++ NEU bis hier ++++++++++++ */

  const onSubmit = async (vals) => {
    try {
      setLoading(true);

      /* --- interne Notizen + Mapping berücksichtigen --- */
      /* SummaryNotes nur bei Note-Optionen behalten – exakt wie Step 4 */
      const effectiveVals = {
        ...vals,
        SummaryNotes: NOTE_OPTIONS.includes(selectedValue) ? vals.SummaryNotes : ''
      };

      const diff     = buildDiff(jobData, effectiveVals);
      const apiDiff  = renameKeys(diff);

      if (!Object.keys(apiDiff).length) {
        showNotification('info', 'Keine Änderungen vorhanden.');
        return;
      }

      const res = await fetch(`${config.host}/jobs/${jobID}`, {
        method:  'PUT',
        headers: { 'Content-Type':'application/json' },
        body:    JSON.stringify(apiDiff)
      });
      if (!res.ok) throw new Error('Update fehlgeschlagen');

      showNotification('success', 'Änderungen gespeichert.');
      navigate('/jobs');
    } catch (err) {
      console.error(err);
      showNotification('error', 'Speichern fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------ */
  /* Weitergabe an Präsentations-Layer                            */
  /* ------------------------------------------------------------ */
  return (
    <EditJobsView
      /* Daten & Flags */
      jobID={jobID}
      loading={loading} jobData={jobData} users={users}
      customerData={customerData} descriptionOptions={descriptionOptions}
      dataState={dataState} sort={sort} imagePreviews={imagePreviews}
      contactPersons={contactPersons} jobDocuments={jobDocuments}
      selectedTab={selectedTab}
      selectedValue={selectedValue} summaryText={summaryText}
      pdfLoading={pdfLoading} mapVisible={mapVisible}
      contactPersonsVisible={contactPersonsVisible}
      employeeAssignmentVisible={employeeAssignmentVisible}
      jobDocumentsVisible={jobDocumentsVisible}
      isGoogleMapsLoaded={isGoogleMapsLoaded}
      radioData={RADIO_OPTIONS} noteOptions={NOTE_OPTIONS} showNotification={showNotification}
      /* Setter */
      formRef={formRef} setMapVisible={setMapVisible}
      setContactPersonsVisible={setContactPersonsVisible}
      setEmployeeAssignmentVisible={setEmployeeAssignmentVisible}
      setJobDocumentsVisible={setJobDocumentsVisible}
      setDataState={setDataState} setSort={setSort}
      setImagePreviews={setImagePreviews} setContactPersons={setContactPersons}
      setJobDocuments={setJobDocuments}
      setSelectedTab={setSelectedTab} setSelectedValue={setSelectedValue}
      setSummaryText={setSummaryText}
      /* Handler */
      handleTabSelect={(e)=>setSelectedTab(e.selected)}
      onCancelClick={()=>navigate('/jobs')}
      onSubmit={onSubmit}
      downloadPdf={downloadPdf}
      removeImage={removeImage}
      onRowAction={onRowAction}
    />
  );
};

export default EditJobsContainer;
